import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useOutletContext } from "react-router-dom";
import { useContentHeightCalculation } from "../../../../../hooks/useContentHeightCalculation";
import BudgetCarousel from "../../../../../components/ui/carousels/BudgetCarousel";
import TransactionCarousel from "../../../../../components/ui/carousels/TransactionCarousel";
import RecurrentTransactionCarousel from "../../../../../components/ui/carousels/RecurrentTransactionCarousel";
import StreamlineSpendingChart from "../../../../../components/ui/charts/finances/StreamlineSpendingChart";
import { useSpendingUsage } from "../../../../../hooks/finances/useSpendingUsage";
import { useBalanceUsage } from "../../../../../hooks/finances/useBalanceUsage";
import { useIncomeUsage } from "../../../../../hooks/finances/useIncomeUsage";
import GhostChart from "../../../../../components/ui/loading/skeletons/GhostChart";
import GhostBar from "../../../../../components/ui/loading/skeletons/GhostBar";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import BaseChartCarousel from "../../../../../components/ui/carousels/common/BaseChartCarousel";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner";
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import HistoryChart from "../../../../../components/ui/charts/HistoryChart";
import TransactionDetailsModal from "../../../../../components/ui/popup/finances/TransactionDetailsModal";

const INCOME_DATE_FILTERS = [
  { label: "Last Month", value: "last_month" },
  { label: "This Month", value: "this_month" },
];

function PersonalOverview() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const { contentRef, mainRef } = useContentHeightCalculation();

  const {
    institutionSyncStates = [],
    loadSyncStates,
    isLoadingSyncStates,
  } = useOutletContext() || {};

  // Responsive chart height calculation (< 960px -> 130px, else -> 280px)
  const [isMobileChartHeight, setIsMobileChartHeight] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileChartHeight(window.innerWidth < 960);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const chartHeight = isMobileChartHeight ? 160 : 280;

  const isEmptyOnboarding = useMemo(() => {
    if (isLoadingSyncStates) return true;
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.transaction_first_load_completed === false,
    );
  }, [institutionSyncStates, isLoadingSyncStates]);

  const isPartialSyncing = useMemo(() => {
    if (
      isLoadingSyncStates ||
      isEmptyOnboarding ||
      institutionSyncStates.length === 0
    )
      return false;
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding, isLoadingSyncStates]);

  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter((inst) => inst.transaction_history_completed === false)
      .map((inst) => inst.institution_name)
      .filter(Boolean);

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  const isGraphDataReady = useMemo(() => {
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === true,
    );
  }, [institutionSyncStates]);

  const spendingUsage = useSpendingUsage(isGraphDataReady);
  const balanceUsage = useBalanceUsage(null, isGraphDataReady);

  const {
    appliedPeriod,
    setAppliedPeriod,
    isLoading: isIncomeLoading,
    isPending: isIncomePending,
    incomeData,
    formattedChartData,
    selectedTransaction,
    setSelectedTransaction,
    fetchIncome,
    handleBarClick,
  } = useIncomeUsage("this_month");

  useEffect(() => {
    if (isGraphDataReady && !isLoadingSyncStates) {
      fetchIncome();
    }
  }, [isGraphDataReady, isLoadingSyncStates, fetchIncome]);

  useEffect(() => {
    if (isGraphDataReady && !isLoadingSyncStates) {
      spendingUsage.refresh();
      balanceUsage.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionSyncStates, isGraphDataReady, isLoadingSyncStates]);

  useRefreshOnAuth(() => {
    if (isGraphDataReady) {
      spendingUsage.refresh();
      balanceUsage.refresh();
      fetchIncome();
    }
  });

  const isInitialCarouselLoading =
    spendingUsage.isLoading || balanceUsage.isLoading;

  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.transactions.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
      />
    );
  }

  return (
    <Fragment>
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.transactions.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}

      {/* COMBINED CHART CAROUSEL */}
      {isGraphDataReady && (
        <LoadingToRealTransitionWrapper
          isLoading={isInitialCarouselLoading}
          ghost={
            <GhostCardContainer>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <GhostBar width="120px" height="24px" />
                <div style={{ display: "flex", gap: "8px" }}>
                  <GhostBar width="28px" height="28px" circle />
                  <GhostBar width="28px" height="28px" circle />
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px", overflow: "hidden" }}>
                <div style={{ width: "85%", flexShrink: 0 }}>
                  <GhostChart />
                </div>
                <div style={{ width: "85%", flexShrink: 0 }}>
                  <GhostChart />
                </div>
              </div>
            </GhostCardContainer>
          }
        >
          <div className="standard-card-container mobile-reset-standard-card-container">
            <BaseChartCarousel
              title={content.overview.money_flow.title}
              previousAlt={content.global.previous}
              nextAlt={content.global.next}
            >
              {/* Slide 1: Money Flow / Spending Chart */}
              <StreamlineSpendingChart data={spendingUsage} />

              {/* Slide 2: Income Chart */}
              <div className="streamline-wrapper">
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div className="income-header-row">
                    <span className="income-label">Income</span>
                    <h2
                      className={`income-amount ${
                        isIncomeLoading ? "is-refreshing" : ""
                      }`}
                    >
                      $
                      {parseFloat(
                        incomeData.summary?.total || 0,
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </h2>
                  </div>

                  {/* DATE FILTER PILLS */}
                  <div className="utc-header-filtering">
                    {INCOME_DATE_FILTERS.map((filter) => {
                      const isActive = appliedPeriod === filter.value;
                      return (
                        <div
                          key={filter.value}
                          className={`utc-header-filtering-item ${
                            isActive ? "active" : ""
                          }`}
                          onClick={() => setAppliedPeriod(filter.value)}
                          style={{ cursor: "pointer" }}
                        >
                          {filter.label}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Localized Ghost Chart Transition */}
                <div
                  style={{ width: "100%", minWidth: 0, overflowX: "hidden" }}
                >
                  {isIncomeLoading || isIncomePending ? (
                    <GhostChart height={chartHeight} />
                  ) : (
                    <HistoryChart
                      key={`${appliedPeriod}-${formattedChartData.length}-${chartHeight}`}
                      data={formattedChartData}
                      height={chartHeight}
                      showLegend={false}
                      showYAxis={false}
                      showXAxisLabels={true}
                      defaultBarColor="#10B981"
                      referenceLines={[]}
                      onBarClick={handleBarClick}
                      emptyMsg="No historical income records available for this period."
                      showBarValues={true}
                    />
                  )}
                </div>
              </div>
            </BaseChartCarousel>
          </div>
        </LoadingToRealTransitionWrapper>
      )}

      <div className="mobile-container-separator"></div>
      <TransactionCarousel />
      <div className="mobile-container-separator"></div>
      <RecurrentTransactionCarousel />
      <div className="mobile-container-separator"></div>
      <BudgetCarousel />

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onTransactionUpdate={() => fetchIncome()}
          copy={content.transactions?.details}
          commonCopy={content.global}
        />
      )}
    </Fragment>
  );
}

export default PersonalOverview;
